import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Spinner } from 'react-bootstrap';
import { ProjectContext } from 'contexts/ProjectContext';
import ApiService from 'helpers/apiService';

// Поддержка → Чат — интеграция operator UI support-chat сервиса через iframe.
// Документация: support-chat/docs/11-admin-panel-integration.md (Variant A
// iframe) + support-chat/docs/13-admin-panel-sso-requirements.md (SSO).
//
// Flow:
//  1. На mount тянем chat-JWT из admin-api POST /chat/v1/sso-token (под
//     отдельным CHAT_JWT_SECRET, отличным от admin-api JWT.Secret).
//  2. Embed iframe operator UI с ?sso_token=<JWT>&project_id=<UUID>.
//  3. При смене ProjectContext.currentProjectId — iframe src обновляется,
//     key={projectId} даёт hard remount (operator UI начинает с чистым
//     state в новом проекте). Токен переиспользуется — claims.projects
//     включают все проекты оператора.
//  4. Если admin-api /chat/v1/sso-token вернул 503 (CHAT_JWT_SECRET не
//     сконфигурирован) или 401 — embed iframe без sso_token; operator UI
//     fallback'нется на свою login-форму. Сообщаем оператору warning'ом.
//
// Phase 2 (chat-team task): operator UI читает ?sso_token из URL и кладёт
// в localStorage. До этого iframe всё равно покажет login-форму даже с
// токеном в URL — мы готовы как только chat-team зарелизит.

const DEFAULT_OPERATOR_URL = 'https://support-chatfrontend-test.up.railway.app';

// v71 (CTO+SA review): hardcoded CSP_ALLOWED_HOSTS убран — устаревал при
// каждом обновлении CSP chat-командой. Теперь используем browser-native
// `securitypolicyviolation` event для runtime-детекции реальной
// блокировки iframe. Если event прилетел с нашим operator-UI origin —
// переключаемся на fallback с кнопкой «Открыть в новой вкладке». Иначе
// iframe рендерится как обычно. Это не зависит от чужого hardcode.

interface SsoResponse {
  data?: {
    token?: string;
    expires_at?: string;
  };
}

// Время ожидания, что operator UI пришлёт postMessage({type: 'ready'}) после
// загрузки iframe. Если за этот период события не пришло — показываем warning
// "operator UI не сообщил о готовности". Раньше (v62-v70) фронт ставил
// ssoState='ok' сразу после получения JWT — оператор видел "Подключение к
// чату…" → молниеносный iframe → не понимал, реально ли установилось
// соединение (см. Bug 5, 2026-06-01).
const IFRAME_READY_TIMEOUT_MS = 15_000;

const SupportChat: React.FC = () => {
  const projectContext = useContext(ProjectContext);
  const currentProjectId = projectContext?.currentProjectId || null;
  const currentProjectName = projectContext?.currentProject?.name || '';

  const operatorUrl =
    (process.env.REACT_APP_CHAT_OPERATOR_URL as string | undefined) ||
    DEFAULT_OPERATOR_URL;

  const operatorOrigin = useMemo(() => {
    try {
      return new URL(operatorUrl).origin;
    } catch {
      return '';
    }
  }, [operatorUrl]);

  const [ssoToken, setSsoToken] = useState<string | null>(null);
  const [ssoState, setSsoState] = useState<'idle' | 'loading' | 'ok' | 'unavailable' | 'auth_error'>('idle');
  const [cspBlocked, setCspBlocked] = useState(false);
  // iframeState — отдельно от ssoState: ssoState отвечает за получение токена
  // от admin-api, iframeState — за реальное состояние operator UI в iframe.
  // 'pending' до тех пор пока iframe не сообщит ready или не получит onLoad
  // event; 'ready' — operator UI готов; 'load_error' — iframe.onerror либо
  // таймаут готовности.
  const [iframeState, setIframeState] = useState<'pending' | 'ready' | 'load_error'>('pending');
  // Счётчик активных диалогов (если operator UI шлёт через postMessage —
  // Bug 2/3, 2026-06-01). Контракт со chat-team описан в комментарии ниже.
  const [activeDialogs, setActiveDialogs] = useState<number | null>(null);
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Runtime CSP detection. browser стреляет 'securitypolicyviolation' event
  // когда iframe блокируется CSP frame-ancestors directive. Перехватываем
  // и переключаемся на fallback (кнопка «Открыть в новой вкладке») вместо
  // молчаливого белого экрана. Раньше тут был hardcode'ный whitelist хостов
  // (admin.test.gsmsoft.eu / admin.gsmsoft.eu), но он устаревал при
  // каждом обновлении CSP chat-командой → false positives на dev origin'ах
  // которые они уже разрешили.
  useEffect(() => {
    const handler = (e: SecurityPolicyViolationEvent) => {
      if (!operatorOrigin) return;
      // blockedURI на iframe-violation = origin (или конкретный URL) того
      // ресурса, который попытались встроить. directive — 'frame-ancestors'
      // на стороне operator UI; но в admin-panel мы видим reverse:
      // 'frame-src'/'frame-ancestors' от того, что Browser посчитал
      // блокированным. Проверяем по blockedURI начало.
      if (typeof e.blockedURI === 'string' && e.blockedURI.startsWith(operatorOrigin)) {
        setCspBlocked(true);
      }
    };
    document.addEventListener('securitypolicyviolation', handler);
    return () => document.removeEventListener('securitypolicyviolation', handler);
  }, [operatorOrigin]);

  // postMessage listener от iframe operator UI. Контракт со chat-команды
  // (см. support-chat/docs/13-admin-panel-sso-requirements.md, Phase 2.5):
  //   - { type: 'ready' } — operator UI смонтировался, WS подключение готово
  //   - { type: 'active-dialogs', count: N } — обновление счётчика активных
  //     диалогов оператора (для badge на навбар-кнопке Чат, Bug 2)
  //   - { type: 'token-expired' } — chat-JWT истёк, фронт может re-fetch'нуть
  //   - { type: 'dialog-event', event: 'created'|'closed'|'message', ... }
  // Origin-проверка обязательна (security): принимаем только от operatorOrigin.
  // Bug 3/5 (2026-06-01): фронт не знал, "поехал" ли operator UI вообще.
  useEffect(() => {
    if (!operatorOrigin) return undefined;
    const handler = (event: MessageEvent) => {
      if (event.origin !== operatorOrigin) return;
      const msg = event.data;
      if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') return;
      switch (msg.type) {
        case 'ready':
          setIframeState('ready');
          if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current);
          break;
        case 'active-dialogs':
          if (typeof msg.count === 'number' && msg.count >= 0) {
            setActiveDialogs(msg.count);
          }
          break;
        case 'token-expired':
          // Сбросим state — следующий useEffect перевыпустит токен. До прихода
          // фикса (SUPPORT-CHAT-4) — оператор перезагружает страницу.
          setSsoState('auth_error');
          break;
        case 'dialog-event':
          // Опционально для chat-team: если они шлют новые события, мы можем
          // прокидывать их дальше в глобальное состояние / уведомлять оператора.
          // Сейчас только обновляем счётчик при том же сообщении.
          break;
        default:
          // Незнакомые типы игнорируем — контракт остаётся forward-compatible.
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [operatorOrigin]);

  // Один раз на mount тянем токен. Срок жизни 24h (по дефолту admin-api
  // ChatJWT.TokenTTL) — на сессию оператора в админке хватает с запасом.
  // Если истечёт — admin-panel reload (logout/login) выдаст свежий.
  useEffect(() => {
    let cancelled = false;
    setSsoState('loading');
    ApiService.post<SsoResponse>('/chat/v1/sso-token', {})
      .then(resp => {
        if (cancelled) return;
        const token = resp?.data?.token;
        if (token) {
          setSsoToken(token);
          setSsoState('ok');
        } else {
          setSsoState('unavailable');
        }
      })
      .catch((err: { response?: { status?: number } }) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 503) setSsoState('unavailable');
        else if (status === 401 || status === 403) setSsoState('auth_error');
        else setSsoState('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const iframeSrc = useMemo(() => {
    if (!currentProjectId) return null;
    const u = new URL(operatorUrl);
    u.searchParams.set('project_id', currentProjectId);
    if (ssoToken) u.searchParams.set('sso_token', ssoToken);
    return u.toString();
  }, [operatorUrl, currentProjectId, ssoToken]);

  // Bug 5 (2026-06-01): iframe lifecycle. onLoad срабатывает когда браузер
  // загрузил document, но это НЕ значит что operator UI готов работать (он
  // мог застрять на JS-инициализации / WS-handshake / chat-JWT валидации).
  // Поэтому: onLoad → ждём postMessage({type:'ready'}) до IFRAME_READY_TIMEOUT_MS.
  // Если ready не пришёл — показываем warning без перерисовки iframe.
  const handleIframeLoad = () => {
    // На каждый load (включая re-load при смене src) запускаем заново таймер.
    if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current);
    readyTimeoutRef.current = setTimeout(() => {
      setIframeState((prev) => (prev === 'pending' ? 'load_error' : prev));
    }, IFRAME_READY_TIMEOUT_MS);
  };
  const handleIframeError = () => {
    if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current);
    setIframeState('load_error');
  };

  // Сбрасываем iframeState при смене проекта (или src) — fresh iframe = fresh
  // ready-handshake.
  useEffect(() => {
    setIframeState('pending');
    setActiveDialogs(null);
    return () => {
      if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current);
    };
  }, [currentProjectId]);

  if (!currentProjectId) {
    return (
      <Card className="mb-3">
        <Card.Body>
          <h3 className="mb-2">Поддержка — Чат</h3>
          <p className="mb-0 text-muted">
            Выберите проект в правом верхнем углу — чат скоупится на активный
            проект.
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (ssoState === 'loading') {
    return (
      <Card className="mb-3">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted">Получение SSO-токена…</span>
        </Card.Body>
      </Card>
    );
  }

  // Bug 6 (2026-06-01): если currentProject.name пуст (например ProjectContext
  // ещё не загрузил справочник), не показываем UUID — это путало оператора.
  // Раньше: `{currentProjectName || currentProjectId}` → UUID на экране.
  // Теперь: пока name пуст — Spinner + «Загрузка проекта…». UUID показываем
  // только в title-tooltip для диагностики.
  const showProjectLoading = !currentProjectName;

  // Статус-индикатор подключения. Bug 5/3: оператор видит реальную фазу:
  //   pending — iframe загружается
  //   ready   — operator UI прислал postMessage({type:'ready'}) → реально подключен
  //   load_error — таймаут или iframe.onerror → проблема со стороны chat-сервиса
  // Note: 'ready' зависит от того, что operator UI шлёт postMessage с
  // правильным контрактом. Если chat-команда ещё не зарелизила handler —
  // статус останется 'pending' до timeout, потом перейдёт в 'load_error'.
  // Это правильное поведение: пользователь увидит конкретную проблему.
  const statusBadge = (() => {
    if (iframeState === 'ready') {
      return (
        <span className="badge bg-success" title="Operator UI готов">
          ● Подключено
        </span>
      );
    }
    if (iframeState === 'load_error') {
      return (
        <span className="badge bg-warning text-dark" title="Operator UI не прислал ready за 15 сек">
          ● Operator UI не отвечает
        </span>
      );
    }
    return (
      <span className="badge bg-secondary" title="Ждём ready от operator UI">
        <Spinner animation="border" size="sm" style={{ width: 10, height: 10, marginRight: 4 }} />
        Подключение…
      </span>
    );
  })();

  return (
    <>
      <Card className="mb-2">
        <Card.Body className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted">Проект:</span>
            {showProjectLoading ? (
              <span className="d-inline-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <span className="text-muted small">Загрузка проекта…</span>
              </span>
            ) : (
              <strong title={currentProjectId || ''}>{currentProjectName}</strong>
            )}
            {activeDialogs !== null && activeDialogs > 0 && (
              <span className="badge bg-primary ms-2" title="Активные диалоги оператора">
                {activeDialogs} активн.
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {statusBadge}
            <span className="small text-muted d-none d-md-inline">
              Чат привязан к активному проекту — при смене переключится.
            </span>
          </div>
        </Card.Body>
      </Card>
      {ssoState === 'unavailable' && (
        <Alert variant="warning" className="py-2 small mb-2">
          SSO для чата не сконфигурирован на admin-api (нужен <code>CHAT_JWT_SECRET</code>).
          Войдите формой operator UI вручную.
        </Alert>
      )}
      {ssoState === 'auth_error' && (
        <Alert variant="warning" className="py-2 small mb-2">
          Сессия admin-panel истекла — переавторизуйтесь, чтобы включить SSO в чате.
        </Alert>
      )}
      {iframeState === 'load_error' && !cspBlocked && (
        <Alert variant="warning" className="py-2 small mb-2">
          <strong>Operator UI не сообщил о готовности за 15 сек.</strong> Возможные причины:
          <ul className="mb-0 mt-1 small">
            <li>
              chat-backend WebSocket падает (CHAT_JWT_SECRET в Railway env не настроен
              или не совпадает с admin-api) — проверьте F12 → Network → WS на
              <code>wss://support-chat-test.up.railway.app/ws/operator</code>;
            </li>
            <li>
              operator UI ещё не реализовал <code>postMessage(&#123;type:&#39;ready&#39;&#125;)</code> после
              успешного WS-handshake (контракт <a href="https://github.com/qtimespace/support-chat/blob/main/docs/13-admin-panel-sso-requirements.md#14-postmessage-контракт-для-admin-panel-sso-iframe-запрос-2026-06-01" target="_blank" rel="noopener noreferrer">doc 13 §14</a>);
            </li>
            <li>
              CSP <code>frame-ancestors</code> на operator UI пока не включает ваш origin
              (<code>{typeof window !== 'undefined' ? window.location.origin : ''}</code>) — chat-команда должна добавить;
            </li>
          </ul>
          Iframe ниже остался — operator UI может работать без real-time обновлений (F5 чтобы увидеть новые диалоги).
        </Alert>
      )}
      {cspBlocked ? (
        <Card>
          <Card.Body className="text-center py-4">
            <Alert variant="warning" className="py-2 small mb-3">
              Браузер заблокировал встраивание operator UI на этом origin{' '}
              (<code>{typeof window !== 'undefined' ? window.location.origin : ''}</code>)
              CSP-директивой <code>frame-ancestors</code>. Откройте чат в новой
              вкладке — это рабочий fallback. Для встраивания iframe — chat-команде
              нужно добавить ваш origin в CSP.
            </Alert>
            <a
              href={iframeSrc as string}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm"
            >
              Открыть operator UI в новой вкладке
            </a>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <iframe
              // key={currentProjectId} даёт hard-remount iframe при смене
              // проекта — гарантия чистого state operator UI (иначе остался
              // бы открытый диалог из предыдущего проекта).
              key={currentProjectId}
              title="Support Chat Operator"
              src={iframeSrc as string}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              style={{
                width: '100%',
                height: 'calc(100vh - 180px)',
                border: 0,
                display: 'block'
              }}
              allow="clipboard-write"
            />
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default SupportChat;
